/**
 * PATH: packages/email-hub/src/feature/lib/dedup.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-DEDUP-001
 * PURPOSE: Canonical body of this email lib module. Lifted
 *          from apps/<tenant>/src/features/email/lib/dedup.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/lib/dedup.ts: thin re-export
 *     shim that points back here.
 */

import type { Email } from '../hooks/useEmails'

/** Email enriched with the aggregated mailbox set after dedup. */
export interface DedupedEmail extends Email {
  /** Every mailbox this conversation copy was found in. Always at
   *  least one entry. ``email`` is the receiving mailbox's address;
   *  ``user_id`` is the Quotech user id (used to colour the chip
   *  consistently across the app). */
  mailboxes: Array<{
    user_id: number | null
    email: string
    name: string
  }>
  /** The full set of email row IDs that were merged into this
   *  survivor — needed for any bulk action ("mark read", "archive")
   *  that should affect every mailbox copy of the same conversation,
   *  not just the visible one. */
  duplicate_ids: string[]
  /** How many distinct mailboxes received this canonical message.
   *  Prefers the SERVER count (`received_in_mailbox_count`, Layer B)
   *  and falls back to the deduped mailbox-set length so the row badge
   *  ("received in N mailboxes") is correct even for legacy rows. */
  received_in_mailbox_count: number
}

/**
 * Backend-authoritative group key (dedup Layer B). Returns a stable
 * `canon:<id>` key when the row carries a backend dedup signal:
 *   - it's an explicit duplicate (`canonical_email_id` set) → group on
 *     the canonical id, OR
 *   - it IS a canonical that other rows point to (`referenced`), OR
 *   - the server already aggregated >1 receiving mailbox onto it.
 * Returns null when the row has NO backend signal, so the caller falls
 * back to the legacy `dedupKey` heuristic for un-backfilled rows.
 */
function canonicalKey(e: Email, referenced: Set<string>): string | null {
  if (e.canonical_email_id) return `canon:${e.canonical_email_id}`
  if (referenced.has(e.id)) return `canon:${e.id}`
  if ((e.received_in_mailbox_count ?? 0) > 1) return `canon:${e.id}`
  return null
}

function dedupKey(e: Email): string {
  // Truncate to the minute (60_000 ms) to absorb cross-mailbox
  // delivery skew. Use UTC seconds since epoch for stable string
  // formatting independent of the user's locale.
  const t = new Date(e.received_at).getTime()
  const minute = Number.isFinite(t) ? Math.floor(t / 60_000) : 0
  const conv = e.conversation_id || ''
  const subjectFingerprint = (e.subject || '').trim().toLowerCase().slice(0, 200)
  const sender = (e.from_address || '').trim().toLowerCase()
  // When we have a conversation id, that + sender + minute is enough
  // — the subject is redundant within a single thread and including
  // it would cause re-thread-renames to break dedup. Without one, we
  // fall back to subject for the join key.
  return conv
    ? `c:${conv}|${sender}|${minute}`
    : `s:${subjectFingerprint}|${sender}|${minute}`
}

function pickSurvivor(group: Email[], currentUserId?: number | null): Email {
  if (group.length === 1) return group[0]
  // Preference order:
  //   1. Row owned by the current viewer (so action plumbing maps
  //      to the row "they" can write to).
  //   2. An unread copy (so read-state on the visible row reflects
  //      "this conversation has unread mail").
  //   3. The most recent copy.
  const own = currentUserId
    ? group.find((e) => e.synced_by_user_id === currentUserId)
    : undefined
  if (own) return own
  const unread = group.find((e) => !e.is_read)
  if (unread) return unread
  return [...group].sort(
    (a, b) =>
      new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
  )[0]
}

/**
 * Dedupe a list of email rows across mailbox copies. Returns the
 * survivor for each unique conversation, enriched with the full
 * mailbox set and duplicate id list.
 *
 * Pure function — same input always returns same output. Used inside
 * a ``useMemo`` in InboxPage so the dedup only re-runs when the raw
 * list changes.
 */
export function dedupEmails(
  emails: Email[],
  currentUserId?: number | null,
): DedupedEmail[] {
  if (!Array.isArray(emails) || emails.length === 0) return []

  // Pre-pass: collect the set of ids that are pointed-to as a canonical
  // by some duplicate in THIS page, so a canonical row groups together
  // with its duplicates even though it has no `canonical_email_id` of
  // its own. (Backend dedup Layer B is authoritative; the legacy
  // `dedupKey` heuristic only fires for rows with no backend signal.)
  const referenced = new Set<string>()
  for (const e of emails) {
    if (e.canonical_email_id) referenced.add(e.canonical_email_id)
  }

  const groups = new Map<string, Email[]>()
  for (const e of emails) {
    const k = canonicalKey(e, referenced) ?? dedupKey(e)
    const arr = groups.get(k)
    if (arr) arr.push(e)
    else groups.set(k, [e])
  }

  const out: DedupedEmail[] = []
  for (const [, group] of groups) {
    const survivor = pickSurvivor(group, currentUserId)
    const mailboxesByEmail = new Map<
      string,
      { user_id: number | null; email: string; name: string }
    >()
    // Prefer the SERVER-aggregated mailbox set (Layer B) carried on any
    // row in the group — it's authoritative and survives pagination
    // (the duplicate copy may be on another page). Fall back to building
    // the set from the rows present in this page.
    const serverSet = group
      .map((e) => e.received_in_mailboxes)
      .find((m) => Array.isArray(m) && m.length > 0)
    if (serverSet) {
      for (const m of serverSet) {
        const key = (m.email || '').toLowerCase()
        if (!key || mailboxesByEmail.has(key)) continue
        mailboxesByEmail.set(key, {
          user_id: m.account_id ?? null,
          email: m.email,
          name: m.name,
        })
      }
    } else {
      for (const e of group) {
        // Prefer the per-MAILBOX identity (the EmailAccount's own address)
        // over the per-USER one so a user who owns two mailboxes shows two
        // distinct chips — falls back to the owner address for legacy rows
        // that were never attributed to an account.
        const email = e.synced_by_mailbox_email || e.synced_by_email || ''
        const name = e.synced_by_mailbox_name || e.synced_by_name || ''
        const key = email.toLowerCase()
        if (!key) continue
        if (!mailboxesByEmail.has(key)) {
          mailboxesByEmail.set(key, {
            user_id: e.synced_by_user_id ?? null,
            email,
            name,
          })
        }
      }
    }
    const mailboxes = Array.from(mailboxesByEmail.values())
    // Server count wins (it spans pages); else the deduped set length.
    const serverCount = group
      .map((e) => e.received_in_mailbox_count ?? 0)
      .reduce((a, b) => Math.max(a, b), 0)
    out.push({
      ...survivor,
      mailboxes,
      duplicate_ids: group.map((e) => e.id),
      received_in_mailbox_count: Math.max(serverCount, mailboxes.length, 1),
    })
  }

  // Re-sort by received_at desc so the dedup pass doesn't disturb
  // the inbox ordering. Same comparator the backend uses on its
  // ``ordering = ['-received_at']`` model meta.
  return out.sort(
    (a, b) =>
      new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
  )
}

/**
 * The HIDDEN sibling copies for a set of visible (survivor) row ids.
 *
 * BUG-2026-06-25-EMAIL-MULTI-MAILBOX-ACTIONS: the inbox dedupes the same
 * conversation arriving in several connected mailboxes into ONE visible
 * row, but an action (read / archive / bin / junk / star) only hits that
 * survivor's id — the other mailbox copies stay unread/active server-side
 * and REAPPEAR after the next sync. `dedupEmails` already recorded every
 * merged id on `duplicate_ids`; this returns the copies that AREN'T in the
 * caller's target set, so the caller can fan the same action out to them.
 *
 * Pure: a survivor with no real duplicates (`duplicate_ids` length < 2)
 * contributes nothing, so a single-mailbox org gets `[]` and behaves
 * exactly as before. Ids already in `targetIds` are never returned.
 */
export function siblingDuplicateIds(
  deduped: Array<Pick<DedupedEmail, 'id' | 'duplicate_ids'>>,
  targetIds: string[],
): string[] {
  if (!Array.isArray(deduped) || deduped.length === 0) return []
  const want = new Set(targetIds)
  if (want.size === 0) return []
  const out = new Set<string>()
  for (const row of deduped) {
    const dups = row.duplicate_ids
    if (!Array.isArray(dups) || dups.length < 2) continue
    // This deduped row is targeted if its survivor id is requested OR any
    // of its merged ids is (the caller may hold a raw id, not the survivor).
    if (want.has(row.id) || dups.some((d) => want.has(d))) {
      for (const d of dups) if (!want.has(d)) out.add(d)
    }
  }
  return Array.from(out)
}

// ── Group-by-mailbox (optional inbox view) ──────────────────────────
//
// The user wanted a unified inbox by default (NOT an Outlook-style
// switcher) but ALSO the option to "group/separate the inbox by
// mailbox" when they have several connected. These pure helpers drive
// that opt-in view: a stable sort that clusters rows by the mailbox
// that received them, plus the key/label a section header reads from.

/** The receiving-mailbox key a row groups under. Prefers the per-row
 *  primary mailbox (first entry of the deduped set), falling back to
 *  the raw `synced_by_*` stamps. Lower-cased email; '' for a legacy
 *  row with no mailbox attribution (sorted/labelled as "Unknown"). */
export function mailboxGroupKeyOf(e: DedupedEmail): string {
  const mb = e.mailboxes && e.mailboxes[0]
  return (
    mb?.email ||
    e.synced_by_mailbox_email ||
    e.synced_by_email ||
    ''
  )
    .trim()
    .toLowerCase()
}

/** Human label for a mailbox group header. Operator (2026-06-18):
 *  show the mailbox ADDRESS, not the friendly name ("MARK NEWMAN"), so
 *  it's obvious which inbox a cluster belongs to. Prefers the raw-cased
 *  address; falls back to the friendly name, then a stable
 *  "Unknown mailbox" bucket. */
export function mailboxGroupLabelOf(e: DedupedEmail): string {
  const mb = e.mailboxes && e.mailboxes[0]
  const name = mb?.name || e.synced_by_mailbox_name || e.synced_by_name || ''
  // Raw-cased address for display (mailboxGroupKeyOf lower-cases for keying).
  const email = (
    mb?.email ||
    e.synced_by_mailbox_email ||
    e.synced_by_email ||
    ''
  ).trim()
  return email || name || 'Unknown mailbox'
}

/** Number of DISTINCT receiving mailboxes across the deduped list
 *  (excludes the unknown bucket). Gates whether the group-by-mailbox
 *  toggle is worth showing at all. */
export function countDistinctMailboxes(emails: DedupedEmail[]): number {
  const keys = new Set<string>()
  for (const e of emails) {
    const k = mailboxGroupKeyOf(e)
    if (k) keys.add(k)
  }
  return keys.size
}

/**
 * Stable-sort a deduped list so rows sharing a receiving mailbox are
 * contiguous (the unknown bucket sinks to the bottom), WITHOUT
 * disturbing the received_at-desc order WITHIN each group. Pure: a new
 * array, input untouched.
 *
 * First-seen group order is preserved (the group whose newest mail is
 * most recent appears first), so the busiest mailbox naturally leads.
 */
export function sortEmailsByMailbox(emails: DedupedEmail[]): DedupedEmail[] {
  if (!Array.isArray(emails) || emails.length === 0) return []
  // Record first-seen index per group key to keep a deterministic,
  // recency-led group order (input is already received_at desc).
  const order = new Map<string, number>()
  emails.forEach((e, i) => {
    const k = mailboxGroupKeyOf(e)
    if (!order.has(k)) order.set(k, i)
  })
  const rank = (e: DedupedEmail): number => {
    const k = mailboxGroupKeyOf(e)
    // Unknown bucket ('') always last.
    if (!k) return Number.MAX_SAFE_INTEGER
    return order.get(k) ?? Number.MAX_SAFE_INTEGER
  }
  // Stable sort by group rank only — equal ranks keep their relative
  // (received_at desc) order, so within-group ordering is preserved.
  return emails
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const r = rank(a.e) - rank(b.e)
      return r !== 0 ? r : a.i - b.i
    })
    .map(({ e }) => e)
}
